'use client'

import { useRouter, usePathname } from 'next/navigation'
import { PropsWithChildren, createContext, useContext, useState } from 'react'
import useNotifications from '@/hooks/useNotifications'
import { reportErrorToDD } from '@/lib/analytics/datadogError'
import {
    classifyInitiateResponse,
    startLatencyTimer,
    trackOtpSendCompleted,
    trackOtpSendRequested,
    type OtpFlow,
} from '@/lib/analytics'
import { useOptionalTenant } from '@/contexts/TenantContext'
import { appPath } from '@/lib/getPublicAppUrl'
import { getCognitoHostedUiLogoutUrl } from '@/lib/cognitoHostedUiLogout'

interface AuthContextData {
    loading: boolean
    login: (phone: string, authType: string) => Promise<boolean>
    logout: () => Promise<void>
    advanceToLoginSignupStep: (step: string) => void
    setPhoneNumber: (phoneNumber: string) => void
    getPhoneNumber: () => string | null
}

const AuthContext = createContext<AuthContextData>({
    loading: false,
    login: async () => false,
    logout: async () => undefined,
    advanceToLoginSignupStep: () => undefined,
    setPhoneNumber: () => undefined,
    getPhoneNumber: () => null,
})

export default function AuthProvider({ children }: PropsWithChildren) {
    const [loading, setLoading] = useState(false)

    const router = useRouter()
    const pathname = usePathname()
    const showNotification = useNotifications()
    const tenant = useOptionalTenant()

    const setPhoneNumber = (newPhoneNumber: string) => {
        localStorage.setItem('phoneNumber', newPhoneNumber)
    }

    const getPhoneNumber = (): string | null => {
        return localStorage.getItem('phoneNumber')
    }

    const login = async (phoneNumber: string, authType: string) => {
        setLoading(true)

        const flow: OtpFlow = authType === 'signup' ? 'signup' : 'login'
        const startedAt = Date.now()
        // Guard against double-emitting otp_send_completed when fetch() resolves
        // with a non-2xx response whose body fails to parse as JSON (the catch
        // below would otherwise re-emit as network_error after the initial
        // status-classified emit has already fired).
        let didEmitSendCompleted = false

        try {
            const response = await fetch(appPath('/auth/initiate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    phoneNumber: phoneNumber,
                    authType: authType,
                }),
            })
            if (process.env.NODE_ENV === 'development') {
                console.log(response.status)
                console.log(response)
            }

            trackOtpSendCompleted({
                flow,
                result: classifyInitiateResponse(response.status),
                statusCode: response.status,
                durationMs: Date.now() - startedAt,
            })
            didEmitSendCompleted = true

            if (response.status == 409) {
                if (tenant?.auth?.signupOnLoginIfPhoneNotFound && authType === 'login') {
                    startLatencyTimer('signup_phone_to_otp')
                    trackOtpSendRequested({ flow: 'signup' })
                    return login(phoneNumber, 'signup')
                }

                router.replace('/get-started')
                setLoading(false)
                showNotification({
                    title: 'We do not recognize this phone number',
                    message: 'It looks like you have not yet signed up. Please sign up to continue.',
                    color: 'black',
                })
                return false
            }

            const data = await response.json()
            if (data.isExistingUser) {
                sessionStorage.setItem('isExistingUser', 'true')
            } else {
                sessionStorage.removeItem('isExistingUser')
            }
        } catch (error: any) {
            console.error('Error during login or signup:', error)
            reportErrorToDD(error, { source: 'AuthContext', action: 'login' })
            if (!didEmitSendCompleted) {
                trackOtpSendCompleted({
                    flow,
                    result: 'network_error',
                    durationMs: Date.now() - startedAt,
                })
            }
            setPhoneNumber('')
            setLoading(false)
            return false
        }

        setLoading(false)

        if (authType === 'login') {
            advanceToLoginSignupStep('/login/verify')
        } else if (authType === 'signup') {
            advanceToLoginSignupStep('/get-started/verify')
        }

        return true
    }

    const logout = async () => {
        try {
            await fetch(appPath('/auth/logout'), { method: 'POST', credentials: 'include' })
            localStorage.removeItem('phoneNumber')
            // Cognito Hosted UI keeps its own session cookie on the Cognito
            // domain. Clearing it via /logout (logout_uri = public origin)
            // prevents the next social authorize from resuming that session.
            const cognitoLogoutUrl = getCognitoHostedUiLogoutUrl()
            if (cognitoLogoutUrl) {
                window.location.assign(cognitoLogoutUrl)
                return
            }
            advanceToLoginSignupStep('/login')
        } catch (error) {
            console.error('Error logging out:', error)
            reportErrorToDD(error, { source: 'AuthContext', action: 'logout' })
        }
    }

    const advanceToLoginSignupStep = (path: string) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('path: ', path)
            console.log('pathname: ', pathname)
        }
        // If the user is on the login page proceeding to any subsequent get-started page we should allow the transition
        if (
            pathname.startsWith('/login') &&
            path.startsWith('/get-started') &&
            !path.startsWith('/welcome') &&
            path.length > 13
        ) {
            localStorage.setItem('currentLoginSignupStep', path)
            router.replace(path)
            // If the user is on the login page and tries to switch the to get-started page they shouldn't be forwarded back to login
        } else if (
            path.startsWith('/login') &&
            pathname.startsWith('/get-started') &&
            !pathname.startsWith('/welcome')
        ) {
            localStorage.setItem('currentLoginSignupStep', path)
            // If the user is on any non-login page they should be forced to OTP when necessary
        } else if (path != pathname) {
            localStorage.setItem('currentLoginSignupStep', path)
            router.replace(path)
        }
    }

    return (
        <AuthContext.Provider
            value={{
                loading,
                login,
                logout,
                advanceToLoginSignupStep,
                setPhoneNumber,
                getPhoneNumber,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
